'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

// Supabase auth integration: uses signInWithPassword
// Admin users must be created in Supabase Auth dashboard

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Extension Officer', email: 'ramesh.sharma@igkvkanker.ac.in', password: 'Admin@IGKV2026' },
  { role: 'Senior Scientist', email: 'anjali.verma@igkvkanker.ac.in', password: 'Science@IGKV26' },
];

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  // Detect ?error=access_denied from AppLayout redirect
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('error') === 'access_denied') {
        setAccessDenied(true);
      }
    }
  }, []);

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        setError('email', {
          message: error.message || 'Invalid credentials. Please check your email and password.',
        });
        setIsLoading(false);
        return;
      }

      // Admin authorization gate: only users with app_metadata.role = "admin" may proceed.
      // Any authenticated user without this role is signed out immediately.
      const role = authData?.user?.app_metadata?.role;
      if (role !== 'admin') {
        await supabase.auth.signOut();
        setError('email', {
          message: 'Admin access required. This dashboard is restricted to authorised university staff.',
        });
        setIsLoading(false);
        return;
      }

      toast.success('Signed in successfully');
      router.push('/');
      router.refresh();
    } catch {
      setError('email', { message: 'An unexpected error occurred. Please try again.' });
      setIsLoading(false);
    }
  };

  const autofill = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-10 xl:p-14"
        style={{ backgroundColor: 'var(--primary)' }}
      >
        <div className="flex items-center gap-3">
          <AppLogo size={40} />
          <div>
            <p className="text-white font-semibold text-lg leading-tight">IGKVAdmin</p>
            <p className="text-white/70 text-sm">Indira Gandhi KVV, Kanker</p>
          </div>
        </div>

        <div>
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-snug mb-4">
            Agricultural Query<br />Resolution System
          </h1>
          <p className="text-white/80 text-base leading-relaxed max-w-md">
            Empowering extension officers to receive, review, and respond to farmer queries —
            connecting field problems to expert solutions.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { label: 'Registered Farmers', value: '1,240+' },
              { label: 'Queries Resolved', value: '3,870+' },
              { label: 'Districts Covered', value: '1' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                <p className="text-white text-2xl font-bold tabular-nums">{stat.value}</p>
                <p className="text-white/70 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/50 text-xs">
          © 2026 Indira Gandhi Krishi Vishwavidyalaya, Kanker, Chhattisgarh
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={36} />
          <span className="font-semibold text-lg" style={{ color: 'var(--foreground)' }}>IGKVAdmin</span>
        </div>

        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>
              Admin Sign In
            </h2>
            <p className="text-sm mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Access the agricultural query resolution dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Access denied banner */}
            {accessDenied && (
              <div
                className="flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
                style={{ borderColor: '#fca5a5', backgroundColor: '#fef2f2', color: '#b91c1c' }}
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <span>Admin access required. This dashboard is restricted to authorised university staff.</span>
              </div>
            )}
            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Institutional Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@igkvkanker.ac.in"
                className="form-input"
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="form-input pr-10"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="form-error">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                id="rememberMe"
                type="checkbox"
                className="w-4 h-4 rounded accent-primary cursor-pointer"
                {...register('rememberMe')}
              />
              <label
                htmlFor="rememberMe"
                className="text-sm cursor-pointer select-none"
                style={{ color: 'var(--muted-foreground)' }}
              >
                Keep me signed in
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5 text-base"
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 3 2.373 3 5.373..." />
                  </svg>
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div
            className="mt-6 rounded-xl border p-4"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--primary-light)' }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-wide mb-3"
              style={{ color: 'var(--primary)' }}
            >
              Demo Accounts
            </p>
            <div className="space-y-2">
              {DEMO_CREDENTIALS.map((cred) => (
                <div
                  key={`demo-${cred.role}`}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-lg border"
                  style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                      {cred.role}
                    </p>
                    <p className="text-xs truncate font-mono-data" style={{ color: 'var(--muted-foreground)' }}>
                      {cred.email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => autofill(cred.email, cred.password)}
                    className="btn-secondary text-xs px-3 py-1.5 flex-shrink-0"
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}